import { Link, useParams, useSearchParams, useLocation } from "react-router-dom";
import usePaginate from "@/components/paginate/usePaginate";
import { useDateFromSearchParams } from "@/helpers/useTimeHooks";
import { useQuery } from "@tanstack/react-query";
import { RelativeComponentLoader } from "@/components/loader";
import { CommonSummary } from "@/pages/query/common/CommonSummary";
import { Stack, Typography } from "@altimateai/lego";
import {
  getStoredProcedureRunsList,
  getStoredProceduresRunsGraphData,
  getStoredProceduresRunsSummary,
} from "@/helpers/apis";
import {
  getDateByEndOfDayWithoutTimeoffset,
  getDateByStartOfDayWithoutTimeoffset,
} from "@/components/dateRange/utils";
import { Graph } from "@/pages/query/Graph";
import { StoredProceduresJobRunsListResponse } from "@/pages/query/types";
import { formatBytes, formatCost, formatMilliseconds } from "@/helpers/utils";
import dayjs from "dayjs";
import { DataTable } from "@/components/tables";
import { Paginate } from "@/components/paginate";
import { useState, useEffect } from "react";
import { useAppState } from "@/modules/app/useAppContext";
import { useBreadcrumb, getEntityName } from "@/components/breadcrumbs";

export const StoredProceduresRunHistory = () => {
  const { currency } = useAppState();
  const { hash } = useParams();
  const { state } = useLocation();
  const [searchParams] = useSearchParams();
  const { page, setPage, pageSize, updatePageSize } = usePaginate({ size: 20 });
  const { startDate, endDate } = useDateFromSearchParams();
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  useBreadcrumb(getEntityName(state) ?? hash ?? "Job Details", {
    defaultParents: [
      { label: "Code", pathname: "/query" },
      { label: "Stored Procedures", pathname: "/query/stored-procedures" },
    ],
  });

  const { data: summaryData, isLoading: isSummaryLoading } = useQuery({
    queryKey: ["stored_procedures_runs_summary", hash, startDate, endDate],
    queryFn: () =>
      getStoredProceduresRunsSummary(hash!, {
        start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
        end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString(),
      }),
    enabled: Boolean(hash),
  });

  const { data: graphData, isLoading: isGraphLoading } = useQuery({
    queryKey: ["stored_procedures_runs_graph_data", hash, startDate, endDate],
    queryFn: () =>
      getStoredProceduresRunsGraphData(hash!, {
        start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
        end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString(),
      }),
    enabled: Boolean(hash),
  });

  const { data, isLoading } = useQuery({
    queryKey: ["stored_procedures_runs", hash, startDate, endDate, page, pageSize],
    queryFn: () =>
      getStoredProcedureRunsList(hash!, {
        start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
        end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString(),
        page: page + 1,
        size: pageSize,
      }),
    enabled: Boolean(hash),
  });

  // Handle successful data fetch (replaces onSuccess callback removed in TanStack Query v5)
  useEffect(() => {
    if (data) {
      setTotalItems(data?.total ?? 0);
      setTotalPages(data?.pages ?? 0);
    }
  }, [data]);

  const tableHeaders = [
    {
      id: 1,
      label: "Run ID",
      uid: "run_id",
      width: 350,
      render: (item: StoredProceduresJobRunsListResponse) => (
        <Link
          className="al-text-brand condense-text cursor-pointer"
          to={{
            pathname: `/query/stored-procedures/${hash!}/${item.query_id}`,
            search: `?run_query=${encodeURIComponent(item.query_summary)}&query_summary=${encodeURIComponent(searchParams.get("query_summary") || "")}`,
          }}
        >
          {item.query_id}
        </Link>
      ),
    },
    {
      id: 2,
      label: "Database",
      uid: "database_name",
      render: (item: StoredProceduresJobRunsListResponse) => (
        <span data-testid="database-cell">{item.database_name ?? "—"}</span>
      ),
    },
    {
      id: 3,
      label: "Run Cost",
      uid: "query_cost",
      render: (item: StoredProceduresJobRunsListResponse) => (
        <div>{formatCost(item.query_cost, currency)}</div>
      ),
    },
    {
      id: 4,
      label: "Bytes Scanned",
      uid: "bytes_scanned",
      render: (item: StoredProceduresJobRunsListResponse) => (
        <div>{formatBytes(item.bytes_scanned)}</div>
      ),
    },
    {
      id: 5,
      label: "Start Time",
      uid: "start_time",
      render: (item: StoredProceduresJobRunsListResponse) => (
        <div>{dayjs(item.start_time).format("YYYY-MM-DD hh:mm a")}</div>
      ),
    },
    {
      id: 6,
      label: "Execution Time",
      uid: "execution_time",
      render: (item: StoredProceduresJobRunsListResponse) => (
        <div>{formatMilliseconds(item.execution_time)}</div>
      ),
    },
  ];

  if (isSummaryLoading) {
    return <RelativeComponentLoader label="Loading..." />;
  }

  return (
    <Stack direction="column" gap={"lg"}>
      {summaryData && <CommonSummary summaryData={summaryData} />}
      <Stack direction={"column"} className={"al-p-4 al-border al-rounded-md"}>
        <Typography variant={"section-heading"}>Runs Cost</Typography>
        <Graph isLoading={isGraphLoading} data={graphData ?? []} />
      </Stack>
      <Stack direction={"column"} gap={"lg"} className={"al-mt-4"}>
        <Typography variant={"section-heading"}>Runs list</Typography>
        <Stack direction={"column"}>
          <DataTable
            isLoading={isLoading}
            header={tableHeaders}
            data={{
              items: data?.items ?? [],
            }}
          />
          <Paginate
            isLoading={isLoading}
            itemCount={totalItems}
            numPages={totalPages}
            page={page}
            pageSize={pageSize}
            onPageClick={setPage}
            onPageSizeChange={updatePageSize}
            showPageSize
          />
        </Stack>
      </Stack>
    </Stack>
  );
};
